/**
 * Small presentation helpers shared across Ember pages. All output is
 * English — web surfaces are English-only (product hard rule).
 */

import type { TellingChannel } from './api';

/**
 * The §0.9 receipt's channel, in words: how this person told it (2026-09-04
 * 拍板 — three doors, text messages no longer folded into writing). The chat
 * composer's mic is dictation and lands as a typed turn, so it is not a
 * fourth door.
 */
export function tellingChannelLabel(channel: TellingChannel | null | undefined): string {
  if (channel === 'voice') return 'Told over a phone call';
  if (channel === 'sms') return 'Told by text';
  return 'Told in writing';
}

/** The whole receipt: "Told by text · September 4, 2026". Empty without a day. */
export function provenanceLine(
  provenance: { told_at: string | null; channel: TellingChannel } | null | undefined,
): string {
  const day = fullDate(provenance?.told_at);
  if (!day) return '';
  return `${tellingChannelLabel(provenance?.channel)} · ${day}`;
}

/** Compact relative time: "just now", "8m ago", "3h ago", "2d ago", "Mar 4". */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 60_000) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Full date for published states: "March 4, 2026". Pinned to UTC so the same
 * Ember shows the same date to every viewer — and, on the SSR'd homepage, so
 * the server's HTML and the browser's hydration render agree (an unpinned
 * toLocaleDateString flips with the viewer's timezone and breaks hydration).
 */
/** The day alone, short: "Aug 11", with the year once it is not this year. Pinned to UTC like fullDate. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return d.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric', timeZone: 'UTC' }
    : { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function fullDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** How many of them, in words: a room by situation holds four, a topic room up to eight (2026-10-26). */
const COUNT_WORDS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'];

/** A count in words up to eight, the numeral past it, '' for none (a room thread's "Only the four of you"). */
export function countWord(n: number): string {
  if (!Number.isInteger(n) || n < 1) return '';
  return COUNT_WORDS[n - 1] ?? String(n);
}

/** "1 Ember" / "3 Embers" etc. */
export function plural(n: number, singular: string, pluralWord?: string): string {
  return `${n} ${n === 1 ? singular : (pluralWord ?? `${singular}s`)}`;
}

export interface ParagraphSlice {
  /** UTF-16 offsets into the raw canonical string. */
  start: number;
  end: number;
  text: string;
}

/**
 * Split canonical plain text into paragraphs on '\n\n' while keeping each
 * paragraph's raw UTF-16 offsets, so annotation ranges (§9.11) can be sliced
 * against the ORIGINAL string. Never normalizes or trims the source.
 */
export function splitParagraphs(text: string): ParagraphSlice[] {
  const out: ParagraphSlice[] = [];
  let pos = 0;
  for (;;) {
    const idx = text.indexOf('\n\n', pos);
    if (idx === -1) {
      out.push({ start: pos, end: text.length, text: text.slice(pos) });
      break;
    }
    out.push({ start: pos, end: idx, text: text.slice(pos, idx) });
    pos = idx + 2;
  }
  const nonEmpty = out.filter((p) => p.text.trim().length > 0);
  return nonEmpty.length > 0 ? nonEmpty : out.slice(0, 1);
}

